// My Team — per-agent client load & pending actions (Calyvra Administrators only)
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users2, Loader2, ChevronRight, AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import portalApi from '../lib/api';
import { toast } from 'sonner';

export default function TeamPage() {
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'My Team · Calyvra';
    portalApi.getTeamOverview()
      .then((d) => setTeam(d.team || []))
      .catch((e) => toast.error(e.message || 'Could not load team'))
      .finally(() => setLoading(false));
  }, []);

  const openAgentClients = (agent) => navigate(`/portal/clients?agent_id=${agent.id}&agent_name=${encodeURIComponent(agent.name || '')}`);

  return (
    <div className="max-w-4xl" data-testid="team-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Users2 className="h-6 w-6 text-sky-600" /> My Team
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Clients assigned and pending actions per agent. Click an agent to see their clients.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : team.length === 0 ? (
          <p className="text-center text-slate-400 py-16" data-testid="team-empty">No agents found.</p>
        ) : (
          <table className="w-full text-sm" data-testid="team-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Agent</th>
                <th className="px-5 py-3 font-medium">Workspace</th>
                <th className="px-5 py-3 font-medium text-center">Assigned clients</th>
                <th className="px-5 py-3 font-medium text-center">Pending actions</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {team.map((a) => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                  onClick={() => openAgentClients(a)} data-testid={`team-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium text-slate-900">{a.name}</div>
                    <div className="text-xs text-slate-400">{a.email}</div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">
                    <div className="flex flex-wrap gap-1">
                      {(a.workspaces || []).map((w, i) => <Badge key={i} variant="outline" className="text-slate-600">{w}</Badge>)}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-center font-semibold text-slate-900" data-testid={`team-assigned-${a.id}`}>{a.assigned_clients}</td>
                  <td className="px-5 py-4 text-center" data-testid={`team-pending-${a.id}`}>
                    {a.pending_actions > 0
                      ? <Badge className="bg-amber-100 text-amber-700 gap-1"><AlertTriangle className="h-3 w-3" />{a.pending_actions}</Badge>
                      : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="px-5 py-4 text-right text-slate-300"><ChevronRight className="h-4 w-4 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
